import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useQuery, useMutation } from "convex/react";
import { api } from "../convex/_generated/api";
import { useAuthData } from "../hooks/useAuthData";
import { CheckInRatingSection } from "../components/CheckIn/SpeedRailzCheckinRatingSection";
import { colors } from "../constants/theme";

const SPARKLE_BG = require("../assets/DisgoSparkleBackground.jpg");

const SCRIM = "rgba(18, 12, 10, 0.52)";

function RecapSparkleBackdrop() {
  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      <Image
        source={SPARKLE_BG}
        style={styles.bgImage}
        resizeMode="cover"
        blurRadius={Platform.OS === "ios" ? 12 : 10}
      />
      <View style={[StyleSheet.absoluteFill, { backgroundColor: SCRIM }]} />
    </View>
  );
}


/**
 * Recap rate step: sparkle backdrop + one consistent dark-glass card + required star rating → CheckIn edit.
 */
export default function RecapRateMomentScreen({
  route,
  navigation,
  momentId: momentIdProp,
  embedded,
}) {
  const momentId = route?.params?.momentId ?? momentIdProp;
  const { authData } = useAuthData();
  const userPrivyDid = authData?.privyDid ?? null;

  const [rating, setRating] = useState(0);
  const [saving, setSaving] = useState(false);

  const momentForEdit = useQuery(
    api.moments.getMomentForEdit,
    userPrivyDid && momentId
      ? { momentId, privyDid: userPrivyDid }
      : "skip"
  );

  const updateMomentMutation = useMutation(api.moments.updateMoment);

  const restaurantName = momentForEdit?.restaurant?.name ?? "Restaurant";

  const goToEdit = useCallback(() => {
    const nav = navigation;
    if (!nav) return;
    if (typeof nav.replace === "function") {
      nav.replace("CheckIn", { momentId, editMoment: true });
    } else if (typeof nav.navigate === "function") {
      nav.navigate("CheckIn", { momentId, editMoment: true });
    }
  }, [navigation, momentId]);

  useEffect(() => {
    if (!momentForEdit?._id) return;
    const r = momentForEdit.rating;
    if (r !== undefined && r >= 1 && r <= 5) {
      goToEdit();
    }
  }, [momentForEdit, goToEdit]);

  const handleBack = useCallback(() => {
    if (navigation?.goBack) navigation.goBack();
  }, [navigation]);

  const handleContinue = useCallback(async () => {
    if (!userPrivyDid || !momentId) {
      Alert.alert("Sign in", "Please sign in to save your rating.");
      return;
    }
    if (!rating || rating < 1) {
      Alert.alert("Rating", "Choose a star rating to continue.");
      return;
    }
    setSaving(true);
    try {
      await updateMomentMutation({
        momentId,
        privyDid: userPrivyDid,
        rating,
      });
      goToEdit();
    } catch (e) {
      console.error(e);
      Alert.alert("Error", e?.message || "Could not save rating.");
    } finally {
      setSaving(false);
    }
  }, [userPrivyDid, momentId, rating, updateMomentMutation, goToEdit]);

  const Inner = embedded ? View : SafeAreaView;
  const innerEdgeProps = embedded ? {} : { edges: ["top", "left", "right", "bottom"] };

  if (!momentId) {
    return (
      <View style={[styles.root, embedded && styles.rootEmbedded]}>
        {!embedded ? <RecapSparkleBackdrop /> : null}
        <Inner style={[styles.safe, embedded && styles.safeEmbedded]} {...innerEdgeProps}>
          <View style={[styles.centerWrap, embedded && styles.centerWrapEmbedded]}>
            <View style={[styles.card, embedded && styles.cardEmbedded]}>
              <Text
                style={[styles.cardTitle, !embedded && styles.textShadowLight, embedded && styles.cardTitleEmbedded]}
              >
                Something went wrong
              </Text>
              <TouchableOpacity style={styles.primaryBtn} onPress={handleBack}>
                <Text style={styles.primaryBtnText}>Go back</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Inner>
      </View>
    );
  }

  const loading = momentForEdit === undefined;
  const missing = momentForEdit === null;

  const backIconColor = embedded ? "#4e2916" : "#FFFFFF";
  const cardTitleStyle = embedded ? styles.cardTitleEmbedded : [styles.cardTitle, styles.textShadowLight];
  const eyebrowStyle = embedded ? styles.eyebrowEmbedded : [styles.eyebrow, styles.textShadowLight];
  const subtitleStyle = embedded ? styles.cardSubtitleEmbedded : [styles.cardSubtitle, styles.textShadowLight];
  const loadingTextStyle = embedded ? styles.loadingTextEmbedded : [styles.loadingText, styles.textShadowLight];

  return (
    <View style={[styles.root, embedded && styles.rootEmbedded]}>
      {!embedded ? <RecapSparkleBackdrop /> : null}

      <Inner style={[styles.safe, embedded && styles.safeEmbedded]} {...innerEdgeProps}>
        <View style={[styles.topBar, embedded && styles.topBarEmbedded]}>
          <TouchableOpacity
            onPress={handleBack}
            style={[styles.backBtn, embedded && styles.backBtnEmbedded]}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            accessibilityRole="button"
            accessibilityLabel="Back"
          >
            <Ionicons name="chevron-back" size={24} color={backIconColor} />
          </TouchableOpacity>
        </View>

        <View style={[styles.centerWrap, embedded && styles.centerWrapEmbedded]}>
          {loading ? (
            <View style={[styles.card, embedded && styles.cardEmbedded]}>
              <ActivityIndicator size="large" color={embedded ? "#6B4C3B" : "#FFFFFF"} />
              <Text style={loadingTextStyle}>Loading bite…</Text>
            </View>
          ) : missing ? (
            <View style={[styles.card, embedded && styles.cardEmbedded]}>
              <Text style={cardTitleStyle}>Couldn&apos;t load this bite</Text>
              <TouchableOpacity style={styles.primaryBtn} onPress={handleBack}>
                <Text style={styles.primaryBtnText}>Go back</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={[styles.card, embedded && styles.cardEmbedded]}>
              <Text style={eyebrowStyle}>Recap</Text>
              <Text style={cardTitleStyle} numberOfLines={2}>
                {restaurantName}
              </Text>
              <Text style={subtitleStyle}>Rate this bite to continue</Text>

              <View style={styles.ratingWrap}>
                <CheckInRatingSection
                  rating={rating}
                  onRatingChange={setRating}
                  accentColor={embedded ? "#4e2916" : "#FFFFFF"}
                />
              </View>

              <TouchableOpacity
                style={[styles.primaryBtn, (rating < 1 || saving) && styles.primaryBtnDisabled]}
                onPress={handleContinue}
                disabled={rating < 1 || saving}
                activeOpacity={0.88}
              >
                {saving ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.primaryBtnText}>Continue</Text>
                )}
              </TouchableOpacity>
            </View>
          )}
        </View>
      </Inner>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.cream,
  },
  rootEmbedded: {
    minHeight: 0,
  },
  safeEmbedded: {
    minHeight: 0,
  },
  topBarEmbedded: {
    paddingBottom: 4,
  },
  backBtnEmbedded: {
    backgroundColor: "transparent",
  },
  centerWrapEmbedded: {
    justifyContent: "flex-start",
    paddingTop: 4,
    paddingHorizontal: 12,
  },
  cardEmbedded: {
    maxWidth: "100%",
    backgroundColor: "#FFFFFF",
    borderColor: "rgba(77, 44, 29, 0.12)",
    borderWidth: 1,
  },
  cardTitleEmbedded: {
    fontSize: 20,
    fontWeight: "700",
    color: "#2d1a12",
    textAlign: "center",
    marginBottom: 8,
  },
  eyebrowEmbedded: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6b5346",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 6,
    textAlign: "center",
  },
  cardSubtitleEmbedded: {
    fontSize: 15,
    color: "#6b5346",
    textAlign: "center",
    marginBottom: 8,
  },
  loadingTextEmbedded: {
    marginTop: 12,
    fontSize: 15,
    color: "#6b5346",
    textAlign: "center",
  },
  bgImage: {
    ...StyleSheet.absoluteFillObject,
    width: "100%",
    height: "100%",
  },
  safe: {
    flex: 1,
  },
  topBar: {
    paddingHorizontal: 8,
    paddingBottom: 8,
  },
  backBtn: {
    alignSelf: "flex-start",
    padding: 8,
    borderRadius: 999,
    backgroundColor: "rgba(0,0,0,0.35)",
  },
  centerWrap: {
    flex: 1,
    justifyContent: "center",
    paddingHorizontal: 20,
    paddingBottom: 24,
  },
  card: {
    borderRadius: 20,
    paddingVertical: 28,
    paddingHorizontal: 20,
    overflow: "hidden",
    maxWidth: 420,
    alignSelf: "center",
    width: "100%",
    backgroundColor: "rgba(22, 18, 15, 0.88)",
    borderWidth: StyleSheet.hairlineWidth * 2,
    borderColor: "rgba(255, 255, 255, 0.22)",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.18,
    shadowRadius: 28,
    elevation: 10,
  },
  textShadowLight: {
    textShadowColor: "rgba(0, 0, 0, 0.55)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  eyebrow: {
    fontSize: 13,
    fontWeight: "600",
    color: "rgba(255, 255, 255, 0.88)",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 6,
    textAlign: "center",
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 8,
  },
  cardSubtitle: {
    fontSize: 15,
    color: "rgba(255, 255, 255, 0.88)",
    textAlign: "center",
    marginBottom: 8,
  },
  ratingWrap: {
    marginTop: 8,
    marginHorizontal: -4,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
    color: "rgba(255, 255, 255, 0.88)",
    textAlign: "center",
  },
  primaryBtn: {
    marginTop: 24,
    backgroundColor: colors.brownDark,
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: "center",
  },
  primaryBtnDisabled: {
    opacity: 0.45,
  },
  primaryBtnText: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "700",
  },
});
